import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  Injectable,
  LOCALE_ID,
  OnDestroy,
  provideAppInitializer,
  provideZoneChangeDetection,
} from "@angular/core";
import { provideRouter, Router, withComponentInputBinding, withRouterConfig } from "@angular/router";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { routes } from "./app.routes";
import { providePrimeNG } from "primeng/config";
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from "@angular/common/http";
import { environment } from "@environments/environment";
import { initializeTranslation, provideTranslation } from "./kernel/translations/translation.config";
import { TranslationService } from "./kernel/translations/translation.service";
import { ELocalizationLang } from "./shared-core/enums/localization-lang.enum";
import { LocalStorageService } from "./kernel/tools/services/local-storage.service";
import { registerLocaleData } from "@angular/common";
import { provideMessage } from "./kernel/tools/message/message.config";
import { provideGraphQL } from "./kernel/graphql/graphql.config";
import { provideConfirmationDialog } from "./kernel/popups/popup/modules/confirmation-dialog/confirmation-dialog.config";
import { JwtModule } from "@auth0/angular-jwt";
import { provideFirebase } from "./kernel/notifications/filrebase.config";
import { pendingRequestsInterceptor$ } from "ng-http-loader";
import { laraTheme } from "@shared-core/config/primeng";
import { sentryUserContextInitializer } from "./shared-core/Initializers/sentry-user-context.Initializer";
import { sentryFeedbackInitializer } from "./shared-core/Initializers/sentry-feedback.Initializer";
import { sentryReportDialogInitializer } from "./shared-core/Initializers/sentry-report-dialog.Initializer";
import { provideFilePreview } from "./kernel/form/modules/file/file.config";
import * as Sentry from "@sentry/angular";
import localeIt from "@angular/common/locales/it";
import { AuthService as KernelAuthService } from "@kernel/tools/services/auth.service";
import { AuthService } from "@app/shared/auth/services/auth.service";
import { errorInterceptor } from "./shared/general/interceptors/error.interceptor";
import { jwtInterceptor } from "./shared-core/interceptors/jwt.interceptor";

const locales: Record<string, () => Promise<{ default: unknown }>> = {
  en: () => import("@angular/common/locales/en"),
  it: () => import("@angular/common/locales/it"),
  fr: () => import("@angular/common/locales/fr"),
  de: () => import("@angular/common/locales/de"),
  es: () => import("@angular/common/locales/es"),
  ar: () => import("@angular/common/locales/ar"),
  zh: () => import("@angular/common/locales/zh"),
};
registerLocaleData(localeIt);
export const loadLocale = () => {
  const localStorage = inject(LocalStorageService);
  const stored = localStorage.get<string>("language");
  const lang = stored && locales[stored] ? stored : ELocalizationLang.IT;
  if (stored !== lang) localStorage.set("language", lang);
  locales[lang]().then(d => registerLocaleData(d.default));
  return lang;
};

@Injectable()
class SentryErrorHandler implements ErrorHandler, OnDestroy {
  private sentryErrorHandler = Sentry.createErrorHandler();
  handleError(error: unknown): void {
    if (error === "Timeout") return;
    this.sentryErrorHandler.handleError(error);
  }
  ngOnDestroy(): void {
    this.sentryErrorHandler.ngOnDestroy();
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => initializeTranslation(inject(TranslationService))),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: "reload",
        paramsInheritanceStrategy: "always",
      })
    ),
    provideMessage(),
    providePrimeNG({
      theme: {
        preset: laraTheme,
        options: {
          darkModeSelector: ".my-app-dark",
        },
      },
    }),
    {
      provide: LOCALE_ID,
      useFactory: loadLocale,
      deps: [LocalStorageService],
    },
    provideGraphQL(environment.graphql_endpoint),
    provideTranslation(),
    ...provideFirebase(),
    provideConfirmationDialog(),
    importProvidersFrom(
      JwtModule.forRoot({
        config: { tokenGetter: _ => null },
      })
    ),
    provideHttpClient(
      withInterceptors([jwtInterceptor, errorInterceptor]),
      withInterceptorsFromDi(),
      withInterceptors([pendingRequestsInterceptor$])
    ),
    AuthService,
    { provide: KernelAuthService, useExisting: AuthService },
    { provide: ErrorHandler, useValue: new SentryErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(() => sentryUserContextInitializer()),
    provideAppInitializer(() => sentryFeedbackInitializer()),
    provideAppInitializer(() => sentryReportDialogInitializer()),
    provideFilePreview(),
  ],
};
